import { useReactiveVar } from '@apollo/client';
import useChargers from 'hooks/useChargers';
import useEVChargingPricingInfo from 'hooks/useEvChargingPricingInfo';
import useIsEvChargingOnlyZone from 'hooks/useIsEvChargingOnlyZone';
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { SafeAreaView, SectionList, StyleSheet } from 'react-native';
import { country } from 'states/persistInStorage';
import { useSearchStore } from 'stores/searchStore';
import { Divider } from 'styles/CommonLayoutStyles';
import { StyledWrapper } from 'styles/ContainerStyles';
import {
  AccessMethod,
  AccessType,
  ChargingStation,
  Maybe,
  ParkZone,
} from 'types/generatedSchemaTypes';
import { getCurrencyString } from 'utils/commonUtils';
import ChargerSectionHeader from './ChargerSectionHeader';
import ChargerSkeleton from './ChargerSkeleton';
import NoChargersFound from './NoChargersFound';
import RenderChargerItem from './RenderChargerItem';

type ChargerListingProps = {
  parkZone?: Maybe<ParkZone>;
  loading?: boolean;
  updateChargerCount?: (count: number) => void;
  onChargerSelection?: (chargingStationId: string) => void;
};

const ChargerListing: React.FC<ChargerListingProps> = ({
  parkZone,
  loading,
  updateChargerCount,
  onChargerSelection,
}) => {
  const countryCode = useReactiveVar(country) || 'FI';
  const zoneCode = parkZone?.commercialZoneView?.zoneCode;
  const { parkingZoneChargingStations } = parkZone || {};
  const [showAll, setShowAll] = useState(false);
  const { chargers } = useChargers(
    parkZone?.parkingZoneChargingStations ?? undefined
  );

  const { selectedConnector } = useSearchStore((state) => state);
  // What the user is starting here: charging or charging alongside
  // a parking session. Keyed on whether the zone has parking Aimo can sell, not
  // on who manages that parking (see useIsEvChargingOnlyZone).
  const { isEvChargingOnlyZone, isResolvingStpPricing } =
    useIsEvChargingOnlyZone(parkZone);
  const { pricingSegmentsEv, EVChargingPricings, EVChargingPricingsLoading } =
    useEVChargingPricingInfo({
      chargingStations:
        (parkingZoneChargingStations as ChargingStation[]) ?? [],
    });

  const sortedPricingSegmentsEv = useMemo(() => {
    const rank = (type?: string | null) => {
      if (type === 'LOCKED') return 2;
      if (type === 'PRIVATE') return 1;
      return 0;
    };
    return [...pricingSegmentsEv].sort(
      (a, b) => rank(a.accessType) - rank(b.accessType)
    );
  }, [pricingSegmentsEv]);

  const [activeCustomerSegmentUid, setActiveCustomerSegmentUid] = useState<
    string | null
  >(sortedPricingSegmentsEv[0]?.uid ?? null);

  const getChargersThatSupportSegment = (segment: any) => {
    const filteredPricing = EVChargingPricings?.filter(
      (pricing) =>
        pricing?.customerSegments?.some(
          (customerSegment) => customerSegment?.uid === segment.uid
        ) || pricing.defaultSegment?.uid === segment.uid
    );
    return chargers.filter((charger) =>
      filteredPricing?.some(
        (pricing) => pricing.chargingLocationId === charger.chargingLocationId
      )
    );
  };

  const sections = sortedPricingSegmentsEv.map((segment) => ({
    data: getChargersThatSupportSegment(segment).filter(Boolean),
    chargingPriceText:
      segment?.notes
        ?.map((note) => note?.text)
        .filter(Boolean)
        .join(' ') ||
      `${segment.chargingPriceKwh} ${getCurrencyString(countryCode)} /kWh`,
    customerSegmentUid: segment.uid ?? '',
    customerSegmentAccessType: segment.accessType as AccessType,
    customerSegmentAccessMethod: segment.accessMethod as AccessMethod,
    customerSegmentTitle: segment.title ?? '',
  }));
  useEffect(() => {
    if (selectedConnector) {
      const section = sections.find((s) =>
        s.data.some(
          (charger) =>
            charger.publicDisplayId === selectedConnector.publicDisplayId
        )
      );
      setActiveCustomerSegmentUid(section?.customerSegmentUid ?? null);
    }
  }, [selectedConnector, sections]);

  // Default to the first segment the first time segments become available. On a cold
  // visit the screen mounts before segments load, so the initial
  // activeCustomerSegmentUid is null; without this it stays null, RenderChargerItem's
  // notActiveSection guard hides every row and the list looks blank (CPS-26841).
  // We auto-select only ONCE: afterwards a null activeCustomerSegmentUid means the
  // user collapsed the open section in ChargerSectionHeader, which must be preserved
  // (CPS-25718). Only a stale non-null selection (segments changed) falls back.
  const didDefaultActiveSegment = useRef(false);
  useEffect(() => {
    if (sortedPricingSegmentsEv.length === 0) {
      return;
    }

    if (!didDefaultActiveSegment.current) {
      didDefaultActiveSegment.current = true;
      if (activeCustomerSegmentUid === null) {
        setActiveCustomerSegmentUid(sortedPricingSegmentsEv[0]?.uid ?? null);
      }
      return;
    }

    const hasStaleSelection =
      activeCustomerSegmentUid !== null &&
      !sortedPricingSegmentsEv.some(
        (segment) => segment.uid === activeCustomerSegmentUid
      );
    if (hasStaleSelection) {
      setActiveCustomerSegmentUid(sortedPricingSegmentsEv[0]?.uid ?? null);
    }
  }, [sortedPricingSegmentsEv, activeCustomerSegmentUid]);

  useEffect(() => {
    updateChargerCount?.(chargers.length);
  }, [chargers, updateChargerCount]);

  // `sections` are built from `pricingSegmentsEv` which `useEVChargingPricingInfo`
  // loads separately from `parkingZoneChargingStations`. The `loading` prop only
  // covers the parkZone/charging-station queries not `EVChargingPricingsLoading`,
  // so once the stations are in but the segments are not, keep showing the
  // ChargerSkeleton otherwise `sections` is empty and the SectionList renders blank.
  const isPreparingSegments =
    parkingZoneChargingStations?.length !== 0 &&
    EVChargingPricingsLoading &&
    pricingSegmentsEv.length === 0;

  // Search shortcut can land here before the parking query resolves;
  // an early tap would commit the user to park-and-charge on a zone with no STP.
  if (loading || isPreparingSegments || isResolvingStpPricing) {
    return (
      <StyledWrapper ml={20} mr={20}>
        <ChargerSkeleton />
        <ChargerSkeleton />
        <ChargerSkeleton lastElement />
      </StyledWrapper>
    );
  }

  const hasNoVisibleChargers =
    parkingZoneChargingStations?.length === 0 ||
    (!EVChargingPricingsLoading && pricingSegmentsEv.length === 0);

  if (!loading && hasNoVisibleChargers) {
    return <NoChargersFound />;
  }

  return (
    <SafeAreaView style={styles.container}>
      <SectionList
        sections={sections}
        keyExtractor={(item, index) => `${item.id}-${index}`}
        stickySectionHeadersEnabled
        ListFooterComponent={
          sections.length === 1 ? null : <Divider size={8} mb={0} mt={0} />
        }
        renderSectionHeader={({ section }) => {
          const sectionIndex = sections.findIndex(
            (s) => s.customerSegmentUid === section.customerSegmentUid
          );
          return (
            <ChargerSectionHeader
              section={section}
              sectionIndex={sectionIndex}
              isSingleSegment={sections.length === 1}
              activeCustomerSegmentUid={activeCustomerSegmentUid}
              setActiveCustomerSegmentUid={setActiveCustomerSegmentUid}
              zoneCode={zoneCode || ''}
            />
          );
        }}
        renderItem={({ item, index, section }) => {
          return (
            <RenderChargerItem
              item={item}
              index={index}
              section={section}
              activeCustomerSegmentUid={activeCustomerSegmentUid || ''}
              onChargerSelection={onChargerSelection}
              pricingSegmentsEv={pricingSegmentsEv}
              sections={sections}
              showAll={showAll}
              setShowAll={setShowAll}
              zoneCode={zoneCode || ''}
              activeType={isEvChargingOnlyZone ? 'CHARGING' : 'PARK_AND_CHARGE'}
            />
          );
        }}
      />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: 'white',
  },
});

export default ChargerListing;
